import { type FormEvent, Fragment, useEffect, useState } from 'react';

import { Backdrop } from '@shared/atoms/backdrop';
import type { RouteElement } from '@shared/router';
import * as history from '@shared/router/location';

import { Button } from '@playbook/atoms/button';
import { Card } from '@playbook/atoms/card';
import { TextInput } from '@playbook/atoms/inputs/text-input';

import { ApiError } from '@portal/pkg/api';
import * as routes from '@portal/pkg/routes';
import { useSession } from '@portal/pkg/session';
import { PortalNavigation } from '@portal/views/_shared/navigation';

import css from './styles.module.css';

export default function LoginPage(): RouteElement {
	const { status, organization, login } = useSession();

	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState<Nullable<string>>(null);
	const [submitting, setSubmitting] = useState(false);

	// The inverse of the route guard: someone already signed in has no reason
	// to be here.
	useEffect(() => {
		if (status !== 'authenticated') {
			return;
		}

		history.replace(organization ? routes.Keys() : routes.Organizations());
	}, [status, organization]);

	async function handleSubmit(event: FormEvent): Promise<void> {
		event.preventDefault();

		setError(null);
		setSubmitting(true);

		try {
			await login(username, password);
		} catch (caught) {
			setError(signInError(caught));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Fragment>
			<Backdrop />
			<PortalNavigation variant="sticky" />
			<main className={css.main}>
				<Card>
					<form className={css.form} onSubmit={handleSubmit}>
						<div className={css.heading}>
							<h1>Sign in</h1>
							<p>
								With your 360Player account. Developer portal access is granted
								per organization.
							</p>
						</div>

						<TextInput
							required
							type="email"
							name="username"
							label="Email"
							value={username}
							status={error ? 'error' : undefined}
							onChange={(event) => setUsername(event.target.value)}
						/>
						<TextInput
							required
							type="password"
							name="password"
							label="Password"
							value={password}
							status={error ? 'error' : undefined}
							message={error ?? undefined}
							onChange={(event) => setPassword(event.target.value)}
						/>

						<Button
							variant="primary"
							type="submit"
							label="Sign in"
							loading={submitting}
							disabled={submitting}
						/>
					</form>
				</Card>
			</main>
		</Fragment>
	);
}

/**
 *	A 403 is the interesting one: the credentials were right and the account
 *	simply holds the portal capability nowhere, which no amount of retrying
 *	fixes.
 */
function signInError(caught: unknown): string {
	if (!(caught instanceof ApiError)) {
		return 'Could not reach the server. Try again.';
	}

	switch (caught.status) {
		case 401:
			return 'That email and password do not match.';
		case 403:
			return 'This account has no developer portal access. Ask an administrator in your organization.';
		case 429:
			return 'Too many attempts. Wait a minute and try again.';
		default:
			return caught.message;
	}
}
